import { Box, Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { Title } from "@src/appV2/redesign/system/ui-components";
import pluralize from "pluralize";

import { Button } from "../../components/Button";
import { InAppAdCard } from "../../Shift/InAppAd/Card";
import { type InferedInAppAdAttributesSchema } from "../../Shift/Open/types";
import { WorkerPaySurveyCard } from "../../WorkerPaySurvey/components/WorkerPaySurveyCard";
import { useShiftDiscoveryUserFiltersContext } from "../Filters/useUserFiltersContext";
import { ListViewBanners } from "./Banners";
import { ContextualEmptyState } from "./ListEmptyStateContextual";
import { WorkerReferralCta } from "./WorkerReferralCta";
import { WorkplaceReferralCta } from "./WorkplaceReferralCta";

interface ListViewPageOpenShiftsEmptyStateProps {
  readonly daysCount: number;
  readonly inAppAd?: InferedInAppAdAttributesSchema;
  readonly workerPaySurveyId?: string;
  readonly children?: React.ReactNode;
  readonly onClearFilters: () => void;
  readonly unfilteredShiftsLength: number;
}

export function ListViewPageOpenShiftsEmptyState(props: ListViewPageOpenShiftsEmptyStateProps) {
  const {
    daysCount,
    inAppAd,
    workerPaySurveyId,
    children,
    onClearFilters,
    unfilteredShiftsLength,
  } = props;
  const { appliedFiltersCount } = useShiftDiscoveryUserFiltersContext();

  const contextualEmptyStateConfig = useCbhFlag(
    CbhFeatureFlag.SHIFT_DISCOVERY_CONTEXTUAL_EMPTY_STATE,
    {
      defaultValue: {
        enabled: false,
        shiftStartRelativeDaysGte: 0,
        shiftStartRelativeDaysLte: 0,
      },
    }
  );

  if (!contextualEmptyStateConfig.enabled) {
    return (
      <LegacyEmptyState
        appliedFiltersCount={appliedFiltersCount}
        daysCount={daysCount}
        inAppAd={inAppAd}
        unfilteredShiftsLength={unfilteredShiftsLength}
        workerPaySurveyId={workerPaySurveyId}
        onClearFilters={onClearFilters}
      >
        {children}
      </LegacyEmptyState>
    );
  }

  return (
    <ContextualEmptyState
      appliedFiltersCount={appliedFiltersCount}
      daysCount={daysCount}
      inAppAd={inAppAd}
      unfilteredShiftsLength={unfilteredShiftsLength}
      workerPaySurveyId={workerPaySurveyId}
      shiftStartRelativeDaysGte={contextualEmptyStateConfig.shiftStartRelativeDaysGte}
      shiftStartRelativeDaysLte={contextualEmptyStateConfig.shiftStartRelativeDaysLte}
      onClearFilters={onClearFilters}
    >
      {children}
    </ContextualEmptyState>
  );
}

interface LegacyEmptyStateProps {
  readonly daysCount: number;
  readonly inAppAd?: InferedInAppAdAttributesSchema;
  readonly workerPaySurveyId?: string;
  readonly children?: React.ReactNode;
  readonly onClearFilters: () => void;
  readonly unfilteredShiftsLength: number;
  readonly appliedFiltersCount: number;
}

function LegacyEmptyState(props: LegacyEmptyStateProps) {
  const {
    daysCount,
    inAppAd,
    workerPaySurveyId,
    children,
    onClearFilters,
    unfilteredShiftsLength,
    appliedFiltersCount,
  } = props;

  const SAFE_SPACE_BOTTOM = 10;

  return (
    <Box sx={{ flex: "1", overflow: "auto", pb: SAFE_SPACE_BOTTOM }}>
      <ListViewBanners />
      <Stack sx={{ flex: "1", p: 5, pt: 10 }} justifyContent="center" spacing={5}>
        <Title semibold variant="h4" component="h3" sx={{ textAlign: "center" }}>
          {appliedFiltersCount > 0
            ? "There are no open shifts matching your filters. Check back later!"
            : `There are no open shifts in the next ${daysCount} ${pluralize("day", daysCount)}`}
        </Title>

        {appliedFiltersCount > 0 && unfilteredShiftsLength > 0 && (
          <Button
            variant="contained"
            size="medium"
            sx={{ width: "fit-content", alignSelf: "center" }}
            onClick={onClearFilters}
          >
            Reset filters to see {unfilteredShiftsLength} shifts
          </Button>
        )}

        <Stack spacing={3} sx={{ pt: 8 }}>
          <WorkerReferralCta />
          <WorkplaceReferralCta />
          {inAppAd && <InAppAdCard inAppAd={inAppAd} sx={{ mx: 5 }} />}
          {workerPaySurveyId && <WorkerPaySurveyCard surveyId={workerPaySurveyId} sx={{ mx: 5 }} />}
        </Stack>
        {children}
      </Stack>
    </Box>
  );
}
